import { PALETTE_COLOURS } from '@futrli/ui-components';
import classNames from 'classnames';
import React from 'react';
import { ReactStripeElements } from 'react-stripe-elements';

import { StripeComponentInputType } from '../types';

import styles from './with-ui-components-styles.scss';

interface State {
  error: ReactStripeElements.ElementChangeResponse['error'] | null;
}

export interface Props extends ReactStripeElements.ElementProps {
  showOnComplete?: boolean;
  containerClassName?: string;
  label?: string | JSX.Element;
}

/**
 * Applies the UIComponent styles to the stripe components.
 * @param {React.ComponentType<ReactStripeElements.ElementProps>} Component
 * @param {StripeComponentInputType} fieldType
 * @returns {React.ComponentType<Props>}
 */
export function withUIComponentsStyles(
  Component: React.ComponentType<ReactStripeElements.ElementProps>,
  fieldType: keyof typeof StripeComponentInputType = StripeComponentInputType.input
): React.ComponentType<Props> {
  return class extends React.Component<Props> {
    public state: Readonly<State> = {
      error: null
    };

    public render(): JSX.Element {
      const { label, classes, containerClassName = '', showOnComplete, style, ...componentProps } = this.props;
      const { error } = this.state;

      return (
        <div className={containerClassName}>
          {label && <label className={classNames('uiTypeLabel', styles.label)}>{label}</label>}

          <Component
            classes={{
              invalid: styles.invalid,
              focus: styles.focused,
              complete: StripeComponentInputType.select !== fieldType && showOnComplete ? styles.completed : '',
              base: styles.billingComponent,
              ...classes
            }}
            style={{
              ...createInputStyle(),
              ...style
            }}
            {...componentProps}
            onChange={this.handleOnChange}
          />

          {error && error.code && (
            <div className={classNames('uiTypeLabelRed', styles.helperText)}>{error.message}</div>
          )}
        </div>
      );
    }

    private handleOnChange = (event: ReactStripeElements.ElementChangeResponse): void => {
      const { onChange } = this.props;

      this.setState(
        {
          error: event.error || null
        },
        () => {
          onChange && onChange(event);
        }
      );
    };
  };
}

/**
 * Overrides the input styles of the Stripe's input component.
 * NOTE: This is just to override the input styles, rest of styles are being done as
 * usual in the scss file.
 *
 * @TODO fonts need to be injected in the stripe component iframe
 *
 * @returns {ReactStripeElements.ElementsOptions} Input styles.
 */
export const createInputStyle = (): ReactStripeElements.ElementsOptions['style'] => {
  const { $fontGreyDark, $fontGreyLight, $tealGrey } = PALETTE_COLOURS;

  return {
    base: {
      fontFamily: `
        -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Ubuntu, Arial, sans-serif
      `,
      fontWeight: 200,
      fontSize: '16px',
      color: $fontGreyLight,
      letterSpacing: '-0.01px',

      ['::placeholder']: {
        color: $tealGrey
      },
      [':focus']: {
        color: $fontGreyDark
      }
    },
    invalid: {
      [':focus']: {
        color: $fontGreyDark
      },

      color: $fontGreyLight
    }
  };
};
